import { useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BUSINESS, IMAGES } from "@/lib/site";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const PROMISES = [
  { title: "Genuine Products Only", text: "Every item we sell is factory sealed, sourced through authorized distribution, and backed by the official manufacturer warranty." },
  { title: "Proper Bill, Every Time", text: "Each purchase comes with an official GST tax invoice that is honored by brand service networks, so your warranty always works." },
  { title: "Best Prices in the Area", text: "Direct retail relationships mean the lowest price points for you, without inflated markups or hidden fees." },
  { title: "Support After the Sale", text: "Need setup help or service assistance? We are reachable by phone, WhatsApp, and in-store — we stand by you after billing." },
];

const RANGE = [
  "Mobile phones, chargers, earbuds and accessories",
  "Refrigerators, washing machines, Smart TVs and air coolers",
  "Mixer grinders, induction cooktops, microwaves and water purifiers",
  "Soundbars, inverters, stabilizers, cables and office electronics",
];

export default function About() {
  useEffect(() => { document.title = "About Us | SUDHEER ELECTRONIC"; }, []);
  return (
    <div className="bg-[#f4f7fa] text-slate-900 antialiased">
      <Navbar />

      <section className="py-16 sm:py-24 bg-white border-b border-slate-200" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div {...fadeUp} className="lg:col-span-7">
              <span className="text-[11px] font-bold text-blue-700 tracking-widest uppercase">About Us</span>
              <h1 className="text-4xl sm:text-5xl font-black text-slate-950 tracking-tight leading-[1.1] mt-3 mb-6">
                The Electronics Shop Your Neighbourhood Trusts.
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mb-4">
                SUDHEER ELECTRONIC is a local electronics shopping and retail store serving everyone in the area —
                young people buying their first smartphone, families upgrading home appliances, and offices and shops
                purchasing in bulk.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed max-w-xl">
                Customers prefer us for three simple reasons: the best prices in the area, genuine products with a
                proper warranty and bill, and friendly after-sales support that does not disappear once you pay.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.15 }} className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-slate-300 shadow-xl aspect-[4/3]">
                <img src={IMAGES.hero} alt="Inside the SUDHEER ELECTRONIC store" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#f4f7fa]" data-testid="about-promise-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <h2 className="text-3xl font-extrabold text-slate-950 tracking-tight">What We Promise Every Customer</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PROMISES.map((p, i) => (
              <motion.div key={p.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} data-testid={`about-promise-${i + 1}`}
                className="p-8 rounded-2xl bg-blue-50/50 border border-blue-100">
                <h3 className="text-lg font-bold text-slate-950 mb-2">{p.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="about-range-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div {...fadeUp} className="lg:col-span-6">
              <h2 className="text-3xl font-extrabold text-slate-950 tracking-tight mb-4">Everything Electronic, Under One Roof</h2>
              <ul className="space-y-3">
                {RANGE.map((r, i) => (
                  <li key={r} data-testid={`about-range-item-${i + 1}`} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-1 w-5 h-5 bg-blue-700 text-white rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                    {r}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-6">
              <div className="rounded-2xl bg-slate-900 text-white p-8 sm:p-10">
                <h3 className="text-xl font-bold mb-3">Visit Us or Reach Out</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Walk into the shop for a live product demonstration, or ask for today's price from wherever you are.
                </p>
                <div className="text-sm space-y-2 mb-6">
                  <p><span className="font-bold text-blue-400">Phone / WhatsApp:</span> {BUSINESS.phoneDisplay}</p>
                  <p><span className="font-bold text-blue-400">Email:</span> <span className="break-words">{BUSINESS.email}</span></p>
                </div>
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp-button"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider rounded-lg transition-colors">
                  <MessageCircle size={16} /> Chat on WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
